import React, { useCallback, useEffect, useMemo, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../ui/Card';
import { RefreshButton } from '../shared/RefreshButton';
import { GoogleConnectCard } from '../shared/GoogleConnectCard';
import { useGoogleAuth } from '../../hooks/useGoogleAuth';
import { gmail, GmailMessage, GmailLabel, GmailAttachment, getHeader, parseSender, extractMessageBody, extractAttachments, formatFileSize, decodeHtmlEntities } from '../../services/gmail';
import { MailIcon, SearchIcon, AlertCircleIcon, XIcon, InboxIcon, ChevronDownIcon, StarIcon, PaperclipIcon, ReplyIcon, ExternalLinkIcon, MaximizeIcon, MinimizeIcon, SendIcon, FileTextIcon, AlertTriangleIcon, MailOpenIcon, TagIcon, FolderIcon, DownloadIcon, ImageIcon, FileIcon, FileSpreadsheetIcon, FileArchiveIcon, Loader2Icon } from 'lucide-react';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 6
  },
  show: {
    opacity: 1,
    y: 0
  }
};
// Built-in folder definitions
const FOLDERS = [{
  key: 'inbox',
  label: 'Recibidos',
  icon: InboxIcon,
  query: 'in:inbox'
}, {
  key: 'starred',
  label: 'Destacados',
  icon: StarIcon,
  query: 'is:starred'
}, {
  key: 'sent',
  label: 'Enviados',
  icon: SendIcon,
  query: 'in:sent'
}, {
  key: 'drafts',
  label: 'Borradores',
  icon: FileTextIcon,
  query: 'in:drafts'
}, {
  key: 'important',
  label: 'Importantes',
  icon: AlertTriangleIcon,
  query: 'is:important'
}, {
  key: 'unread',
  label: 'No leídos',
  icon: MailOpenIcon,
  query: 'is:unread'
}, {
  key: 'all',
  label: 'Todos',
  icon: FolderIcon,
  query: ''
}] as const;
// Helper: get icon for attachment mime type
function getAttachmentIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return ImageIcon;
  if (mimeType.includes('pdf')) return FileTextIcon;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv')) return FileSpreadsheetIcon;
  if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('tar') || mimeType.includes('gzip')) return FileArchiveIcon;
  return FileIcon;
}
// Email body renderer component
function EmailBodyRenderer({
  messageId,
  isExpanded
}: {
  messageId: string;
  isExpanded: boolean;
}) {
  const [bodyContent, setBodyContent] = useState<{
    html: string;
    plain: string;
  } | null>(null);
  const [attachments, setAttachments] = useState<GmailAttachment[]>([]);
  const [bodyLoading, setBodyLoading] = useState(false);
  const [bodyError, setBodyError] = useState<string | null>(null);
  const [isFullHeight, setIsFullHeight] = useState(false);
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());
  const iframeRef = useRef<HTMLIFrameElement>(null);
  useEffect(() => {
    if (!isExpanded || bodyContent) return;
    let cancelled = false;
    setBodyLoading(true);
    setBodyError(null);
    gmail.getFullMessage(messageId).then(fullMsg => {
      if (cancelled) return;
      const body = extractMessageBody(fullMsg);
      const atts = extractAttachments(fullMsg);
      setBodyContent(body);
      setAttachments(atts);
      setBodyLoading(false);
    }).catch(err => {
      if (cancelled) return;
      setBodyError(err.message || 'Error al cargar el contenido');
      setBodyLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isExpanded, messageId, bodyContent]);
  const handleDownload = async (attachment: GmailAttachment) => {
    setDownloadingIds(prev => new Set(prev).add(attachment.attachmentId));
    try {
      await gmail.downloadAttachment(messageId, attachment);
    } catch (err) {
      console.error('Error downloading attachment:', err);
    } finally {
      setDownloadingIds(prev => {
        const next = new Set(prev);
        next.delete(attachment.attachmentId);
        return next;
      });
    }
  };
  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc?.body) {
        const height = doc.body.scrollHeight;
        iframe.style.height = `${Math.min(isFullHeight ? 9999 : 500, Math.max(120, height + 20))}px`;
      }
    } catch {
      // Cross-origin — ignore
    }
  }, [isFullHeight]);
  if (bodyLoading) {
    return <div className="space-y-3 py-3 px-4" data-id="element-4574">
        <div className="h-3 bg-slate-800/60 rounded w-3/4 animate-pulse" data-id="element-4575" />
        <div className="h-3 bg-slate-800/60 rounded w-full animate-pulse" data-id="element-4576" />
        <div className="h-3 bg-slate-800/60 rounded w-5/6 animate-pulse" data-id="element-4577" />
        <div className="h-24 bg-slate-800/40 rounded-lg animate-pulse mt-2" data-id="element-4578" />
      </div>;
  }
  if (bodyError) {
    return <div className="py-4 text-center" data-id="element-4579">
        <p className="text-sm text-slate-500" data-id="element-4580">{bodyError}</p>
        <button onClick={() => {
        setBodyContent(null);
        setBodyError(null);
      }} className="mt-2 text-xs text-red-400 hover:text-red-300 transition-colors" data-id="element-4581">
          Reintentar
        </button>
      </div>;
  }
  if (!bodyContent) return null;
  const hasHtml = bodyContent.html.trim().length > 0;
  const hasPlain = bodyContent.plain.trim().length > 0;
  // Attachments section (shared between HTML and plain text views)
  const attachmentsSection = attachments.length > 0 ? <div className="mt-4 pt-4 border-t border-slate-800/50" data-id="element-4582">
        <div className="flex items-center gap-2 mb-3" data-id="element-4583">
          <PaperclipIcon size={14} className="text-slate-400" data-id="element-4584" />
          <span className="text-xs font-semibold text-slate-400" data-id="element-4585">
            {attachments.length} adjunto{attachments.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-4586">
          {attachments.map(att => {
        const AttIcon = getAttachmentIcon(att.mimeType);
        const isDownloading = downloadingIds.has(att.attachmentId);
        const isImage = att.mimeType.startsWith('image/');
        return <button key={att.attachmentId} onClick={() => handleDownload(att)} disabled={isDownloading} className="group flex items-center gap-2.5 px-3 py-2 bg-slate-900/80 hover:bg-slate-800/80 border border-slate-800 hover:border-slate-700 rounded-lg transition-all max-w-[220px] disabled:opacity-60" data-id="element-4587">
                <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${isImage ? 'bg-purple-500/15 text-purple-400' : att.mimeType.includes('pdf') ? 'bg-red-500/15 text-red-400' : att.mimeType.includes('spreadsheet') || att.mimeType.includes('excel') ? 'bg-green-500/15 text-green-400' : att.mimeType.includes('zip') ? 'bg-amber-500/15 text-amber-400' : 'bg-slate-800 text-slate-400'}`} data-id="element-4588">
                  <AttIcon size={16} data-id="element-4589" />
                </div>
                <div className="flex-1 min-w-0 text-left" data-id="element-4590">
                  <p className="text-xs text-slate-300 truncate font-medium" data-id="element-4591">
                    {att.filename}
                  </p>
                  <p className="text-[10px] text-slate-600" data-id="element-4592">
                    {formatFileSize(att.size)}
                  </p>
                </div>
                <div className="shrink-0 w-5 h-5 flex items-center justify-center" data-id="element-4593">
                  {isDownloading ? <Loader2Icon size={13} className="text-slate-500 animate-spin" data-id="element-4594" /> : <DownloadIcon size={13} className="text-slate-600 group-hover:text-slate-300 transition-colors" data-id="element-4595" />}
                </div>
              </button>;
      })}
        </div>
      </div> : null;
  if (hasHtml) {
    const cssReset = ['* { box-sizing: border-box; color: #cbd5e1 !important; }', 'body { margin: 0; padding: 16px; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px; line-height: 1.6; color: #cbd5e1 !important; background: transparent !important; word-wrap: break-word; overflow-wrap: break-word; }', 'a, a * { color: #f87171 !important; }', 'img { max-width: 100%; height: auto; border-radius: 4px; }', 'table { max-width: 100% !important; width: auto !important; }', 'td, th, tr { background-color: transparent !important; }', 'pre, code { background: #1e293b !important; padding: 2px 6px; border-radius: 4px; font-size: 13px; color: #e2e8f0 !important; }', 'blockquote { border-left: 3px solid #334155 !important; margin: 8px 0; padding: 4px 12px; color: #94a3b8 !important; }', 'hr { border: none !important; border-top: 1px solid #1e293b !important; margin: 16px 0; }', 'h1, h2, h3, h4, h5, h6 { color: #e2e8f0 !important; margin-top: 16px; margin-bottom: 8px; }', 'p { margin: 0 0 12px 0; color: #cbd5e1 !important; }', 'span, div, li, b, strong, em, i, u, font { color: #cbd5e1 !important; }', 'b, strong { color: #e2e8f0 !important; }', '[style] { background-color: transparent !important; }', 'font[color] { color: #cbd5e1 !important; }'].join('\n');
    const styleOpen = '<' + 'style>';
    const styleClose = '</' + 'style>';
    const wrappedHtml = '<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">' + styleOpen + cssReset + styleClose + '</head><body>' + bodyContent.html + '</body></html>';
    return <div className="relative" data-id="element-4596">
        <div className={`relative rounded-lg overflow-hidden border border-slate-800/60 bg-slate-950/50 ${!isFullHeight ? 'max-h-[500px]' : ''}`} data-id="element-4597">
          <iframe ref={iframeRef} srcDoc={wrappedHtml} sandbox="allow-same-origin" className="w-full border-0 bg-transparent" style={{
          minHeight: '120px',
          height: '300px'
        }} onLoad={handleIframeLoad} title="Email content" data-id="element-4598" />

          {!isFullHeight && <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-950 to-transparent pointer-events-none" data-id="element-4599" />}
        </div>
        <div className="flex justify-center mt-3" data-id="element-4600">
          <button onClick={() => setIsFullHeight(!isFullHeight)} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 bg-slate-900 hover:bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-800 transition-colors" data-id="element-4601">
            {isFullHeight ? <MinimizeIcon size={12} data-id="element-4602" /> : <MaximizeIcon size={12} data-id="element-4603" />}
            {isFullHeight ? 'Colapsar' : 'Ver completo'}
          </button>
        </div>
        {attachmentsSection}
      </div>;
  }
  if (hasPlain) {
    return <div data-id="element-4604">
        <div className={`relative rounded-lg border border-slate-800/60 bg-slate-950/50 p-4 ${!isFullHeight ? 'max-h-[400px] overflow-hidden' : ''}`} data-id="element-4605">
          <pre className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap font-sans m-0" data-id="element-4606">
            {bodyContent.plain}
          </pre>
          {!isFullHeight && bodyContent.plain.length > 800 && <>
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-950 to-transparent pointer-events-none" data-id="element-4607" />
              <div className="flex justify-center mt-3 relative z-10" data-id="element-4608">
                <button onClick={() => setIsFullHeight(true)} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 bg-slate-900 hover:bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-800 transition-colors" data-id="element-4609">
                  <MaximizeIcon size={12} data-id="element-4610" /> Ver completo
                </button>
              </div>
            </>}
        </div>
        {attachmentsSection}
      </div>;
  }
  return <p className="text-sm text-slate-500 italic py-2" data-id="element-4611">
      No se pudo extraer el contenido de este correo.
    </p>;
}
export function GmailPanel() {
  const {
    isConnected,
    isLoading: authLoading,
    signIn
  } = useGoogleAuth();
  const [messages, setMessages] = useState<GmailMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissedError, setDismissedError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedMsg, setExpandedMsg] = useState<string | null>(null);
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [labels, setLabels] = useState<GmailLabel[]>([]);
  const [labelsLoading, setLabelsLoading] = useState(false);
  // Fetch labels on connect
  useEffect(() => {
    if (!isConnected) return;
    setLabelsLoading(true);
    gmail.getLabels().then(allLabels => {
      // Only show user-created labels (type === 'user')
      const userLabels = allLabels.filter(l => l.type === 'user');
      setLabels(userLabels);
      setLabelsLoading(false);
    }).catch(() => setLabelsLoading(false));
  }, [isConnected]);
  const fetchData = async () => {
    if (!isConnected) return;
    setLoading(true);
    setError(null);
    setDismissedError(false);
    try {
      // Build query from active folder
      const folder = FOLDERS.find(f => f.key === activeFolder);
      let query = folder?.query || '';
      // If it's a user label, use label: query
      if (!folder) {
        const label = labels.find(l => l.id === activeFolder);
        if (label) query = `label:${label.name.replace(/\s+/g, '-')}`;
      }
      // Append search query
      if (searchQuery) query = query ? `${query} ${searchQuery}` : searchQuery;
      const [msgs, unread] = await Promise.all([gmail.getMessages(15, query || undefined), gmail.getUnreadCount()]);
      setMessages(msgs);
      setUnreadCount(unread);
    } catch (err: any) {
      setError(err.message || 'Error al cargar correos');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (isConnected) {
      const timeoutId = setTimeout(() => fetchData(), 300);
      return () => clearTimeout(timeoutId);
    }
  }, [isConnected, searchQuery, activeFolder]);
  if (authLoading) {
    return <div className="h-full bg-[#020617] flex items-center justify-center" data-id="element-4612">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" data-id="element-4613" />
      </div>;
  }
  if (!isConnected) {
    return <GoogleConnectCard title="Gmail" description="Conectá tu cuenta de Google para ver correos importantes y dar contexto al agente sobre comunicaciones con clientes." accentColor="red" highlightService="gmail" signIn={signIn} error={error} isLoading={authLoading} data-id="element-4614" />;
  }
  return <div className="h-full flex bg-[#020617] relative overflow-hidden" data-id="element-4615">
      {/* Gmail-style Sidebar */}
      <div className="w-52 shrink-0 border-r border-slate-800/60 flex flex-col overflow-y-auto bg-slate-950/50" data-id="element-4616">
        {/* Folders */}
        <div className="p-3 space-y-0.5" data-id="element-4617">
          {FOLDERS.map(({
          key,
          label,
          icon: Icon,
          query
        }) => {
          const isActive = activeFolder === key;
          return <button key={key} onClick={() => {
            setActiveFolder(key);
            setExpandedMsg(null);
          }} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? 'bg-red-500/15 text-red-400 font-semibold' : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'}`} data-id="element-4618">
                <Icon size={16} className={isActive ? 'text-red-400' : 'text-slate-500'} data-id="element-4619" />

                <span className="truncate" data-id="element-4620">{label}</span>
                {key === 'inbox' && unreadCount > 0 && <span className={`ml-auto text-xs font-bold ${isActive ? 'text-red-400' : 'text-slate-500'}`} data-id="element-4621">
                    {unreadCount}
                  </span>}
              </button>;
        })}
        </div>

        {/* Separator */}
        <div className="mx-3 my-2 h-px bg-slate-800/60" data-id="element-4622" />

        {/* User Labels */}
        <div className="p-3 space-y-0.5" data-id="element-4623">
          <p className="px-3 text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-2" data-id="element-4624">
            Etiquetas
          </p>
          {labelsLoading ? <div className="space-y-2 px-3" data-id="element-4625">
              {[1, 2, 3].map(i => <div key={i} className="h-3 bg-slate-800/40 rounded animate-pulse" data-id="element-4626" />)}
            </div> : labels.length === 0 ? <p className="px-3 text-xs text-slate-600" data-id="element-4627">Sin etiquetas</p> : labels.map(label => {
          const isActive = activeFolder === label.id;
          return <button key={label.id} onClick={() => {
            setActiveFolder(label.id);
            setExpandedMsg(null);
          }} className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-xs transition-colors ${isActive ? 'bg-red-500/15 text-red-400 font-semibold' : 'text-slate-500 hover:bg-slate-800/50 hover:text-slate-300'}`} data-id="element-4628">
                  <TagIcon size={12} className={isActive ? 'text-red-400' : 'text-slate-600'} data-id="element-4629" />

                  <span className="truncate" data-id="element-4630">{label.name}</span>
                  {label.messagesUnread > 0 && <span className="ml-auto text-[10px] text-slate-600" data-id="element-4631">
                      {label.messagesUnread}
                    </span>}
                </button>;
        })}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0" data-id="element-4632">
        {/* Search Bar */}
        <div className="shrink-0 px-4 py-3 border-b border-slate-800/60 flex items-center gap-3" data-id="element-4633">
          <div className="relative flex-1 max-w-lg" data-id="element-4634">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-4635" />

            <input type="text" placeholder="Buscar correos..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-900/50 border border-slate-800 rounded-lg py-2 pl-9 pr-4 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-red-500/50 focus:ring-1 focus:ring-red-500/50 transition-all" data-id="element-4636" />
          </div>
          <RefreshButton onRefresh={fetchData} isRefreshing={loading} data-id="element-4637" />
          <span className="text-xs text-slate-600" data-id="element-4638">
            {messages.length} correos
          </span>
        </div>

        {/* Error Banner */}
        <AnimatePresence data-id="element-4639">
          {error && !dismissedError && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="mx-4 mt-3 p-3 bg-red-950/40 border border-red-900/50 rounded-xl flex items-center gap-3 text-red-400" data-id="element-4640">
              <AlertCircleIcon size={16} className="shrink-0" data-id="element-4641" />
              <p className="text-xs flex-1" data-id="element-4642">{error}</p>
              <button onClick={() => setDismissedError(true)} className="p-1 hover:bg-red-900/50 rounded transition-colors" data-id="element-4643">
                <XIcon size={14} data-id="element-4644" />
              </button>
            </motion.div>}
        </AnimatePresence>

        {/* Messages List */}
        <div className="flex-1 overflow-y-auto" data-id="element-4645">
          {loading && messages.length === 0 ? <div data-id="element-4646">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(i => <div key={i} className="px-4 py-3 border-b border-slate-800/30" data-id="element-4647">
                  <div className="flex items-center gap-3" data-id="element-4648">
                    <div className="h-3 bg-slate-800/50 rounded w-28 animate-pulse" data-id="element-4649" />
                    <div className="h-3 bg-slate-800/50 rounded flex-1 animate-pulse" data-id="element-4650" />
                    <div className="h-3 bg-slate-800/50 rounded w-12 animate-pulse" data-id="element-4651" />
                  </div>
                </div>)}
            </div> : messages.length === 0 ? <div className="flex flex-col items-center justify-center py-24 text-center" data-id="element-4652">
              <InboxIcon size={36} className="text-slate-700 mb-4" data-id="element-4653" />
              <h3 className="text-sm font-medium text-slate-400 mb-1" data-id="element-4654">
                Sin correos
              </h3>
              <p className="text-xs text-slate-600" data-id="element-4655">
                No hay correos en esta carpeta.
              </p>
            </div> : <motion.div variants={containerVariants} initial="hidden" animate="show" data-id="element-4656">
              {messages.map((msg, idx) => {
            const subject = getHeader(msg, 'Subject') || '(Sin asunto)';
            const fromHeader = getHeader(msg, 'From');
            const {
              name: senderName,
              email: senderEmail
            } = parseSender(fromHeader || '');
            const date = msg.internalDate ? new Date(parseInt(msg.internalDate)) : new Date();
            const now = new Date();
            const isToday = date.toDateString() === now.toDateString();
            const formattedDate = isToday ? date.toLocaleTimeString('es-AR', {
              hour: '2-digit',
              minute: '2-digit'
            }) : date.toLocaleDateString('es-AR', {
              day: 'numeric',
              month: 'short'
            });
            const fullDate = date.toLocaleString('es-AR', {
              dateStyle: 'full',
              timeStyle: 'short'
            });
            const isUnread = msg.labelIds?.includes('UNREAD');
            const isStarred = msg.labelIds?.includes('STARRED');
            const hasAttachment = msg.payload?.parts?.some(p => p.filename);
            const isExpanded = expandedMsg === msg.id;
            const isLast = idx === messages.length - 1;
            return <motion.div variants={itemVariants} key={msg.id} data-id="element-4657">
                    <div onClick={() => setExpandedMsg(isExpanded ? null : msg.id)} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors
                        ${isExpanded ? 'bg-slate-800/50' : 'hover:bg-slate-800/20'}
                        ${isUnread && !isExpanded ? 'bg-slate-900/40' : ''}
                        ${!isLast && !isExpanded ? 'border-b border-slate-800/30' : ''}
                      `} data-id="element-4658">
                      {/* Unread dot */}
                      <div className="w-2 shrink-0 flex justify-center" data-id="element-4659">
                        {isUnread && <div className="w-1.5 h-1.5 rounded-full bg-red-500" data-id="element-4660" />}
                      </div>

                      {/* Star */}
                      <div className="w-4 shrink-0" data-id="element-4661">
                        {isStarred && <StarIcon size={13} className="text-amber-400 fill-amber-400" data-id="element-4662" />}
                      </div>

                      {/* Sender */}
                      <span className={`w-36 shrink-0 truncate text-[13px] ${isUnread ? 'font-bold text-slate-100' : 'text-slate-400'}`} data-id="element-4663">
                        {senderName}
                      </span>

                      {/* Subject + snippet */}
                      <div className="flex-1 min-w-0 flex items-center gap-1.5" data-id="element-4664">
                        <span className={`shrink-0 text-[13px] max-w-[45%] truncate ${isUnread ? 'font-semibold text-slate-200' : 'text-slate-300'}`} data-id="element-4665">
                          {subject}
                        </span>
                        <span className="text-slate-700 shrink-0" data-id="element-4666">—</span>
                        <span className="text-[13px] text-slate-600 truncate" data-id="element-4667">
                          {decodeHtmlEntities(msg.snippet)}
                        </span>
                      </div>

                      {/* Attachment */}
                      {hasAttachment && <PaperclipIcon size={13} className="text-slate-600 shrink-0" data-id="element-4668" />}

                      {/* Date */}
                      <span className={`text-xs shrink-0 w-14 text-right font-mono ${isUnread ? 'text-slate-200 font-semibold' : 'text-slate-500'}`} data-id="element-4669">
                        {formattedDate}
                      </span>

                      {/* Chevron */}
                      <ChevronDownIcon size={13} className={`text-slate-700 shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-4670" />
                    </div>

                    {/* Expanded body */}
                    <AnimatePresence data-id="element-4671">
                      {isExpanded && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} className="overflow-hidden" data-id="element-4672">
                          <div className={`bg-slate-950/60 ${!isLast ? 'border-b border-slate-800/30' : ''}`} data-id="element-4673">
                            {/* Info bar */}
                            <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-800/30" data-id="element-4674">
                              <div className="flex items-center gap-3 min-w-0" data-id="element-4675">
                                <span className="text-sm font-medium text-slate-300 shrink-0" data-id="element-4676">
                                  {senderName}
                                </span>
                                <span className="text-xs text-slate-600 truncate" data-id="element-4677">
                                  &lt;{senderEmail}&gt;
                                </span>
                                <span className="text-xs text-slate-700" data-id="element-4678">
                                  → mí
                                </span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0" data-id="element-4679">
                                <span className="text-[11px] text-slate-600 font-mono" data-id="element-4680">
                                  {fullDate}
                                </span>
                                <a href={`https://mail.google.com/mail/u/0/#inbox/${msg.id}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-2.5 py-1 bg-red-500/15 hover:bg-red-500/25 text-red-400 text-xs font-medium rounded-md transition-colors" onClick={e => e.stopPropagation()} data-id="element-4681">
                                  <ReplyIcon size={12} data-id="element-4682" /> Responder
                                </a>
                                <a href={`https://mail.google.com/mail/u/0/#inbox/${msg.id}`} target="_blank" rel="noopener noreferrer" className="p-1 text-slate-600 hover:text-slate-300 transition-colors" onClick={e => e.stopPropagation()} title="Abrir en Gmail" data-id="element-4683">
                                  <ExternalLinkIcon size={13} data-id="element-4684" />
                                </a>
                              </div>
                            </div>
                            {/* Body */}
                            <div className="p-5" data-id="element-4685">
                              <EmailBodyRenderer messageId={msg.id} isExpanded={isExpanded} data-id="element-4686" />
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </motion.div>;
          })}
            </motion.div>}
        </div>
      </div>
    </div>;
}